/**
 * Timezone helpers for the event-date form.
 *
 * The hard timezone direction (wall-clock → instant) is owned by the backend,
 * which already holds TZInfo and the canonical epoch storage. The frontend only
 * does the easy direction (instant → wall-clock) and builds the list of
 * selectable IANA zones. No timezone library is pulled in — everything here is
 * built on the platform `Intl` API.
 */

// `Intl.supportedValuesOf` is not in the TypeScript 4.9 lib definitions yet
// (added in TS 5.1), so declare it here. It is still guarded at runtime because
// older browsers (pre-Chrome 99 / Firefox 93 / Safari 15.4) lack it.
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace, no-unused-vars
  namespace Intl {
    // eslint-disable-next-line no-unused-vars
    function supportedValuesOf(key: 'timeZone'): string[]
  }
}

export interface TimezoneOption {
  value: string
  label: string
}

/** Resolve the browser's IANA timezone id (e.g. `Europe/Oslo`). */
export const getBrowserTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone

/**
 * Build a `(UTC±HH:MM)` suffix for a zone, computed at the given instant.
 * Returns an empty string when the offset cannot be resolved.
 */
const getOffsetLabel = (timeZone: string, at: Date = new Date()): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longOffset',
    }).formatToParts(at)

    const name = parts.find((part) => part.type === 'timeZoneName')?.value
    if (!name) return ''

    // `longOffset` yields e.g. `GMT+02:00`; UTC itself yields the bare `GMT`.
    const normalized = name.replace('GMT', 'UTC')
    return normalized === 'UTC' ? '(UTC+00:00)' : `(${normalized})`
  } catch {
    return ''
  }
}

/**
 * Full list of selectable IANA zones with offset-annotated labels, e.g.
 * `{ value: 'Europe/Oslo', label: 'Europe/Oslo (UTC+02:00)' }`.
 *
 * Falls back to just the browser zone when `Intl.supportedValuesOf` is
 * unavailable or throws (older browsers). The browser zone is always present
 * and de-duplicated, and the API's alphabetical (region-grouped) order is
 * preserved.
 */
export const buildTimezoneOptions = (): TimezoneOption[] => {
  const browserTimeZone = getBrowserTimeZone()

  let identifiers: string[] = []
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      identifiers = Intl.supportedValuesOf('timeZone')
    }
  } catch {
    identifiers = []
  }

  if (identifiers.length === 0) {
    return [{ value: browserTimeZone, label: browserTimeZone }]
  }

  // Ensure the browser zone is selectable even if absent from the list.
  if (!identifiers.includes(browserTimeZone)) {
    identifiers = [browserTimeZone, ...identifiers]
  }

  return identifiers.map((value) => {
    const offset = getOffsetLabel(value)
    return { value, label: offset ? `${value} ${offset}` : value }
  })
}

/**
 * Render a UTC instant as the `YYYY-MM-DDTHH:mm` string a `datetime-local`
 * input expects, expressed in `timeZone`'s wall-clock time.
 *
 * Uses `'en-CA'` with `hourCycle: 'h23'`: `'en-US'` + `hour12: false` historically
 * emits hour `"24"` at midnight on some V8/JSC builds, producing an invalid value
 * like `...T24:00`. Pinning `h23` + `en-CA` avoids that.
 *
 * Empty/invalid input → `''`. A falsy `timeZone` falls back to the browser zone.
 */
export const instantToZonedInputValue = (
  iso: string,
  timeZone: string,
): string => {
  if (!iso) return ''

  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZone || getBrowserTimeZone(),
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(date)

  const lookup = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ''

  const year = lookup('year')
  const month = lookup('month')
  const day = lookup('day')
  const hour = lookup('hour')
  const minute = lookup('minute')

  return `${year}-${month}-${day}T${hour}:${minute}`
}
